/**
 * Navbar Composable
 * 管理導航欄配置
 * 原始來源: cuora-module/src/machine/navbar.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/ui/navbar.js)
 * - API：使用 useFetch 支援 SSR，透過 onResponse 同步處理資料
 * - 數據格式：Base64 編碼的 JSON，需要解碼
 * - 單例模式：使用 nuxtApp._navbarContext 避免 storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'
import { decode } from 'js-base64'
import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { useNavbarStore } from '@/stores/ui/navbar'
import { commonApi, gameApi } from '@/api'

export const useNavbar = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useFetch 和 storeToRefs 只創建一次
  if (!nuxtApp._navbarContext) {
    const { $request } = nuxtApp
    const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public

    const store = useNavbarStore()

    const decodeNavbarConfig = (configString) => {
      if (!configString) {
        console.warn('[useNavbar] configString is empty, returning default config')
        return { settings: [] }
      }

      try {
        const base64Url = configString.split('.')[0]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const decoded = decode(base64)
        return JSON.parse(decoded)
      } catch (error) {
        console.error('[useNavbar] decodeNavbarConfig error:', error)
        return { settings: [] }
      }
    }

    const filterGameCodes = (navbarSettings) => {
      return navbarSettings.reduce((acc, item) => {
        if (item.type?.toLowerCase() === 'game' && item.gameCode) {
          acc.push(item.gameCode)
        }
        return acc
      }, [])
    }

    const getGameInfo = async (gameCodeList) => {
      try {
        const response = await $request.send(gameApi.getV3GameInfo({
          appSecret,
          gameCodes: gameCodeList
        }))
        const data = response.data || response
        return data.data || []
      } catch (err) {
        console.error('[useNavbar] getGameInfo error:', err)
        throw err
      }
    }

    const combineGames = (navbarSettings, gameInfo) => {
      return navbarSettings.map((item) => {
        if (item.type?.toLowerCase() === 'game') {
          const currentGame = gameInfo.find((game) => game.gameCode === item.gameCode)
          return {
            ...item,
            ...currentGame
          }
        } else {
          return item
        }
      })
    }

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 從 commonApi 取得 API 配置
    const apiConfig = commonApi.getNavbar({ appSecret })
    const apiPath = apiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    useFetch(`${baseUrl}/${apiPath}`, {
      key: 'navbar-config',
      dedupe: 'defer',
      query: apiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      async onResponse({ response }) {
        if (response._data?.config) {
          const navbarConfig = decodeNavbarConfig(response._data.config)
          const settings = navbarConfig.settings || []

          store.setNavbar(settings)

          // 檢查是否有遊戲類型的項目，如果有則取得遊戲資訊
          const hasGames = settings.some((item) => item.type?.toLowerCase() === 'game')

          if (hasGames) {
            const extractedGameCodes = filterGameCodes(settings)
            store.setGameCodes(extractedGameCodes)

            if (extractedGameCodes.length > 0) {
              try {
                const gameInfo = await getGameInfo(extractedGameCodes)
                if (!nuxtApp._navbarContext) return
                store.setNavbar(combineGames(settings, gameInfo))
              } catch (err) {
                console.error('[useNavbar] Failed to get game info:', err)
              }
            }
          }
        }
        store.setLoading(false)
      },
      onResponseError({ error: err }) {
        console.error('[useNavbar] fetchNavbar API error:', err)
        store.setError(err)
        store.setLoading(false)
      },
    })

    // storeToRefs 只調用一次
    nuxtApp._navbarContext = {
      ...storeToRefs(store),
    }
  }

  return nuxtApp._navbarContext
}
